<script setup>
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useToast } from 'primevue/usetoast'
import api from '@/services/axios'
import { nairaSymbol } from '@/constants'
import { formatCurrency } from '@/utils'

const route = useRoute()
const toast = useToast()
const breakdown = ref(null)
const loading = ref(true)
const error = ref(null)

const fetchBreakdown = async () => {
  try {
    loading.value = true
    const response = await api.get(`/api/admin/business-payment-breakdown/${route.params.id}`)
    breakdown.value = response.data
  } catch (err) {
    error.value = err.message
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to fetch payment breakdown',
      life: 3000,
    })
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchBreakdown()
})
</script>

<template>
  <div class="py-12">
    <div class="mx-auto max-w-7xl sm:px-6 lg:px-8">
      <div v-if="loading" class="flex items-center justify-center">
        <div class="w-8 h-8 border-4 border-blue-500 rounded-full animate-spin border-t-transparent"></div>
      </div>

      <div v-else-if="error" class="p-4 text-red-700 bg-red-100 rounded-lg">
        {{ error }}
      </div>

      <div v-else-if="breakdown" class="overflow-hidden bg-white shadow sm:rounded-lg">
        <div class="px-4 py-5 sm:px-6">
          <h3 class="text-lg font-medium leading-6 text-gray-900">Business Payment Breakdown</h3>
          <p class="max-w-2xl mt-1 text-sm text-gray-500">Detailed breakdown of payment for order #{{ breakdown.order_id }}</p>
        </div>

        <div class="border-t border-gray-200">
          <dl>
            <!-- Business Information -->
            <div class="px-4 py-5 bg-gray-50 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt class="text-sm font-medium text-gray-500">Business Name</dt>
              <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{{ breakdown.business_name }}</dd>
            </div>

            <div class="px-4 py-5 bg-white sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt class="text-sm font-medium text-gray-500">Business Age</dt>
              <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {{ breakdown.business_age_days }} days
                <span class="ml-2 text-sm" :class="breakdown.is_more_than_month_old ? 'text-green-600' : 'text-yellow-600'">
                  ({{ breakdown.is_more_than_month_old ? 'More than a month old' : 'Less than a month old' }})
                </span>
              </dd>
            </div>

            <!-- Order Information -->
            <div class="px-4 py-5 bg-gray-50 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt class="text-sm font-medium text-gray-500">Order Date</dt>
              <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {{ new Date(breakdown.order_date).toLocaleDateString() }}
              </dd>
            </div>

            <div class="px-4 py-5 bg-white sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt class="text-sm font-medium text-gray-500">Order Status</dt>
              <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{{ breakdown.order_status }}</dd>
            </div>

            <div class="px-4 py-5 bg-gray-50 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt class="text-sm font-medium text-gray-500">Payment Status</dt>
              <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{{ breakdown.payment_status }}</dd>
            </div>

            <!-- Payment Breakdown -->
            <div class="px-4 py-5 bg-white sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt class="text-sm font-medium text-gray-500">Subtotal</dt>
              <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{{ nairaSymbol }}{{ breakdown.subtotal.toFixed() }}</dd>
            </div>

            <div class="px-4 py-5 bg-gray-50 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt class="text-sm font-medium text-gray-500">Business Service Fee</dt>
              <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {{ nairaSymbol }}{{ breakdown.business_service_fee.toFixed() }}
                <span v-if="breakdown.business_service_fee_percentage" class="ml-2 text-sm text-gray-500">
                  ({{ breakdown.business_service_fee_percentage * 100 }}%)
                </span>
              </dd>
            </div>

            <div class="px-4 py-5 bg-gray-50 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt class="text-sm font-medium text-gray-500">Net Payout to Business</dt>
              <dd class="mt-1 text-sm font-semibold text-gray-900 sm:mt-0 sm:col-span-2">{{ nairaSymbol }}{{ breakdown.net_payout.toFixed() }}</dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  </div>
</template>
